import api from './api';

export interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'auditor' | 'manager';
  active: boolean;
  last_login?: string;
  created_at: string;
  business_id?: number;
  business_name?: string;
  assigned_businesses?: number;
  total_audits?: number;
  audits_this_month?: number;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  active?: boolean | string;
}

export interface UserStats {
  total_users: number;
  auditors: number;
  managers: number;
  admins: number;
  active_users: number;
}

const userService = {
  // List users with filters
  async list(filters: UserFilters = {}) {
    const response = await api.get('/users', { params: filters });
    return response.data;
  },

  // Get user by ID
  async getById(id: number): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Create new user
  async create(data: Partial<User> & { password: string }) {
    const response = await api.post('/users', data);
    return response.data;
  },

  // Update user
  async update(id: number, data: Partial<User>) {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  // Delete user (soft delete)
  async delete(id: number) {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  // Toggle user active status
  async toggleStatus(id: number) {
    const response = await api.patch(`/users/${id}/toggle-status`);
    return response.data;
  },

  // Update own profile
  async updateProfile(data: Partial<User>) {
    const response = await api.put('/users/profile', data);
    return response.data;
  },

  // Change password
  async changePassword(data: { currentPassword: string; newPassword: string }) {
    const response = await api.put('/users/change-password', data);
    return response.data;
  },

  // Get businesses assigned to a user
  async getAssignedBusinesses(userId: number) {
    const response = await api.get(`/users/${userId}/businesses`);
    return response.data;
  },

  // Assign business to user
  async assignBusiness(userId: number, businessIds: number | number[]) {
    const payload = Array.isArray(businessIds) 
      ? { business_ids: businessIds } 
      : { business_id: businessIds };
    const response = await api.post(`/users/${userId}/assign-business`, payload);
    return response.data;
  },

  // Remove business assignment from user
  async removeBusinessAssignment(userId: number, businessId: number) {
    const response = await api.delete(`/users/${userId}/unassign-business/${businessId}`);
    return response.data;
  },

  // Get user statistics
  async getStats(): Promise<UserStats> {
    const response = await api.get('/users/stats');
    return response.data;
  },
};

export default userService;
